<template>
  <div>
    <!-- E38 · Los campos que el Diseño Instruccional le agrega a una evaluación del Programa.
         Viven acá y no en cada formulario porque `MatterProgramEvaluationForm` y su gemelo de
         EPM son dos archivos casi idénticos: duplicarlos ahí sería repetir en el frontend la
         misma duplicación que E37 eliminó en el modelo.

         Sólo hay campos que NO existen ya en el formulario del Programa. Los que sí existían
         —participación, instrucciones al estudiante, Recursos de Apoyo, Ambientes, IL— no se
         repiten: se destaparon allá relajando su `v-if`. -->
    <b-form-group
      :label="`${$t('teaching.instructional.activityMomentsField')}:`"
      label-for="select-evaluation-moments"
      label-cols="0"
      label-cols-sm="3"
      class="p-0"
    >
      <p v-if="momentOptions.length === 0" class="text-muted small mb-0">
        {{ $t("teaching.instructional.momentsMissing") }}
      </p>
      <v-select
        v-else
        id="select-evaluation-moments"
        v-model="evaluation.moments"
        :options="momentOptions"
        :reduce="(momentOptions) => momentOptions.id"
        :placeholder="
          $t('reusable.common.selectOneOrMany', {
            entity: $t(
              'teaching.instructional.activityMomentsField'
            ).toLowerCase(),
          })
        "
        multiple
        :selectable="(option) => !evaluation.moments.includes(option.id)"
        label="label"
        track-by="id"
        size="sm"
      >
        <template v-slot:no-options="{ search, searching }">
          <template v-if="searching">
            {{ $t("reusable.common.noOptions") }} "<em>{{ search }}</em
            >"
          </template>
        </template>
      </v-select>
      <b-form-text v-if="momentOptions.length > 0">
        {{
          $t("teaching.instructional.activityMomentsHint", {
            activity: activityEntityName,
            container: containerEntityName,
          })
        }}
      </b-form-text>
    </b-form-group>

    <b-form-group
      :label="`${$t('teaching.instructional.summaryStatus')}:`"
      label-for="select-evaluation-instructional-status"
      label-cols="4"
      label-cols-sm="3"
      class="label"
    >
      <b-form-select
        id="select-evaluation-instructional-status"
        v-model="evaluation.instructional_status"
        :options="statusOptions"
        value-field="id"
        text-field="label"
        size="sm"
      >
        <template #first>
          <b-form-select-option :value="null">
            {{ $t("teaching.instructional.statusNotStarted") }}
          </b-form-select-option>
        </template>
      </b-form-select>
    </b-form-group>

    <!-- Los contenidos ofrecidos son los de las Unidades de Estudio que salen de los IL que la
         evaluación ya declara (E5: el RA define sus contenidos). Por eso este campo depende del
         selector de IL que vive en el formulario del Programa, no acá. -->
    <b-form-group
      :label="`${$t('teaching.instructional.activityContents')}:`"
      label-for="select-evaluation-contents"
      label-cols="0"
      label-cols-sm="3"
      class="p-0"
    >
      <p v-if="contentOptions.length === 0" class="text-muted small mb-0">
        {{
          $t("teaching.instructional.activityContentsEmpty", {
            indicator: $getVisibleNames(
              "teaching.evaluationcriteriamicro",
              true,
              "Indicadores de Logro"
            ),
            study_unit: $getVisibleNames(
              "teaching.studyunit",
              true,
              "Unidades de Estudio"
            ),
          })
        }}
      </p>
      <v-select
        v-else
        id="select-evaluation-contents"
        v-model="evaluation.contents"
        :options="contentOptions"
        :reduce="(contentOptions) => contentOptions.id"
        :placeholder="
          $t('reusable.common.selectOneOrMany', {
            entity: $t('teaching.instructional.activityContents').toLowerCase(),
          })
        "
        multiple
        :selectable="(option) => !evaluation.contents.includes(option.id)"
        label="label"
        track-by="id"
        size="sm"
      >
        <template v-slot:no-options="{ search, searching }">
          <template v-if="searching">
            {{ $t("reusable.common.noOptions") }} "<em>{{ search }}</em
            >"
          </template>
        </template>
      </v-select>
    </b-form-group>

    <!-- Las Estrategias se editan en su propio modal: el catálogo puede ser largo y desde ahí
         se pueden crear nuevas. -->
    <b-form-group
      :label="`${$getVisibleNames(
        'mesh.methodologicalstrategy',
        true,
        'Estrategias Metodológicas'
      )}:`"
      label-cols="0"
      label-cols-sm="3"
      class="p-0"
    >
      <div class="kl-strategy-box">
        <div v-if="selectedStrategies.length > 0" class="kl-strategy-chips">
          <span
            v-for="strategy in selectedStrategies"
            :key="`selected-strategy-${strategy.id}`"
            class="kl-chip"
            :title="strategy.name"
          >
            {{ strategy.name }}
          </span>
        </div>
        <span v-else class="text-muted small">
          {{
            $t("teaching.instructional.activityStrategiesNone", {
              entity: $getVisibleNames(
                "mesh.methodologicalstrategy",
                true,
                "Estrategias Metodológicas"
              ),
            })
          }}
        </span>
        <ButtonSmall
          class="ml-auto"
          :tooltip_text="
            $t('reusable.common.editTooltip', {
              entity: $getVisibleNames(
                'mesh.methodologicalstrategy',
                true,
                'Estrategias Metodológicas'
              ),
            })
          "
          :click_button="() => $bvModal.show(strategiesModalId)"
          :icon="'square'"
        ></ButtonSmall>
      </div>
    </b-form-group>

    <b-form-group
      :label="`${$getVisibleNames(
        'manual.evidencia_de_desempeño',
        true,
        'Evidencias de Desempeño'
      )}:`"
      label-for="select-evaluation-procedure-evidences"
      label-cols="0"
      label-cols-sm="3"
      class="p-0"
    >
      <p
        v-if="procedureEvidenceOptions.length === 0"
        class="text-muted small mb-0"
      >
        {{
          $t("teaching.instructional.activityEvidencesEmpty", {
            entity: $getVisibleNames(
              "manual.evidencia_de_desempeño",
              true,
              "Evidencias de Desempeño"
            ),
          })
        }}
      </p>
      <v-select
        v-else
        id="select-evaluation-procedure-evidences"
        class="kl-chip-select"
        v-model="evaluation.procedure_evidences"
        :options="procedureEvidenceOptions"
        :reduce="(procedureEvidenceOptions) => procedureEvidenceOptions.id"
        :placeholder="
          $t('reusable.common.selectOneOrMany', {
            entity: $getVisibleNames(
              'manual.evidencia_de_desempeño',
              true,
              'Evidencias de Desempeño'
            ).toLowerCase(),
          })
        "
        multiple
        :selectable="
          (option) => !evaluation.procedure_evidences.includes(option.id)
        "
        label="name"
        track-by="id"
        size="sm"
      >
        <template v-slot:option="option">
          <span :title="option.tooltip">{{ option.name }}</span>
        </template>
        <template v-slot:no-options="{ search, searching }">
          <template v-if="searching">
            {{ $t("reusable.common.noOptions") }} "<em>{{ search }}</em
            >"
          </template>
        </template>
      </v-select>
      <b-form-text>
        {{
          $t("teaching.instructional.activityEvidencesHint", {
            entity: activityEntityName,
          })
        }}
      </b-form-text>
    </b-form-group>

    <b-form-group
      :label="`${$t('teaching.instructional.activityProfessorInstructions')}:`"
      label-cols="0"
      label-cols-sm="3"
      class="p-0"
    >
      <div
        class="form-description mb-2"
        @click="$bvModal.show(professorModalId)"
      >
        <RenderVHtml
          v-b-tooltip.v-secondary.noninteractive.hover="{
            title: $t('reusable.common.editDescription'),
            boundary: 'viewport',
          }"
          :content="evaluation.professor_instructions"
        ></RenderVHtml>
      </div>
      <ContainerRichTextKlv3
        :id_modal="professorModalId"
        :title_modal="
          $t('teaching.instructional.activityProfessorInstructions')
        "
        :size_modal="`lg`"
        :object_rich_text="evaluation"
        :content_rich_text="evaluation.professor_instructions"
        :permit_blank="true"
        @save="onProfessorInstructionsSaved"
        @close="$bvModal.hide(professorModalId)"
      >
      </ContainerRichTextKlv3>
    </b-form-group>

    <b-modal
      :id="strategiesModalId"
      :title="`${$t('reusable.common.select')} ${$getVisibleNames(
        'mesh.methodologicalstrategy',
        true,
        'Estrategias Metodológicas'
      )}`"
      size="lg"
      hide-footer
      ignore-enforce-focus-selector="#sidebar-right"
      @show="strategies_modal_key++"
    >
      <ActivityMethodologicalStrategies
        :key="strategies_modal_key"
        :selected="evaluation.methodological_strategies"
        :matter_strategy_ids="matterStrategyIds"
        :can_create="canCreateStrategies"
        :unique_id="unique_id"
        @saved="onStrategiesSaved"
        @cancel="$bvModal.hide(strategiesModalId)"
      ></ActivityMethodologicalStrategies>
    </b-modal>
  </div>
</template>

<script>
import { mapGetters } from "vuex";
import * as names from "@/store/names";
import vSelect from "vue-select";
import "vue-select/dist/vue-select.css";

export default {
  name: "EvaluationInstructionalFields",
  components: {
    vSelect,
    RenderVHtml: () => import("@/components/reusable/RenderVHtml"),
    ContainerRichTextKlv3: () =>
      import("@/components/reusable/ContainerRichTextKlv3"),
    ActivityMethodologicalStrategies: () =>
      import(
        "@/components/teaching/Instructional/components/ActivityMethodologicalStrategies"
      ),
    ButtonSmall: () =>
      import("@/components/reusable/Buttons/Small/ButtonSmall"),
  },
  props: {
    // El objeto del formulario que se está editando. Se muta directo, que es como ya trabajan
    // los dos formularios del Programa con su `evaluation`.
    evaluation: {
      type: Object,
      required: true,
    },
    // La Asignatura base: de ella salen los IL y los contenidos.
    matter_id: {
      type: Number,
      default: null,
    },
    // El ancla del diseño. Se pasa una u otra según el nivel, igual que en el resto del módulo.
    egress_profile_matter_id: {
      type: Number,
      default: null,
    },
    // Distingue los modales cuando hay varias evaluaciones montadas a la vez.
    unique_id: {
      type: [Number, String],
      default: "new",
    },
  },
  data() {
    return {
      strategies_modal_key: 0,
    };
  },
  computed: {
    activityEntityName() {
      return this.$getVisibleNames(
        "manual.instructional_design.activity",
        false,
        "Actividad"
      );
    },
    containerEntityName() {
      return this.$getVisibleNames(
        "manual.instructional_design.container",
        false,
        "Módulo"
      );
    },
    ...mapGetters({
      matters: names.MATTERS,
      instructional_designs: names.INSTRUCTIONAL_DESIGNS,
      instructional_methods: names.INSTRUCTIONAL_METHODS,
      instructional_statuses: names.INSTRUCTIONAL_STATUSES,
      methodologicalStrategies: names.METHODOLOGICAL_STRATEGIES,
      procedureEvidences: names.PROCEDURE_EVIDENCES,
      evaluationCriteriasMicros: names.EVALUATION_CRITERIAS,
      study_unit_contents: names.CONTENTS,
      study_units: names.STUDY_UNITS,
      competence_units: names.COMPETENCE_UNITS,
    }),
    // El diseño de esta asignatura. Es 1:1 con el ancla, así que no hace falta guardarlo en la
    // evaluación: se resuelve acá.
    instructionalDesign() {
      if (this.egress_profile_matter_id) {
        return (
          this.instructional_designs.find(
            (x) => x.egress_profile_matter == this.egress_profile_matter_id
          ) || null
        );
      }
      if (this.matter_id) {
        return (
          this.instructional_designs.find((x) => x.matter == this.matter_id) ||
          null
        );
      }
      return null;
    },
    designMethod() {
      const design = this.instructionalDesign;
      if (!design || !design.method) return null;
      return (
        this.instructional_methods.find((x) => x.id == design.method) || null
      );
    },
    momentOptions() {
      if (!this.designMethod) return [];
      return [...(this.designMethod.moments || [])]
        .sort((a, b) => a.order - b.order)
        .map((moment) => ({ id: moment.id, label: moment.name }));
    },
    // Un estado dado de baja sigue ofreciéndose si la evaluación ya lo tenía: si no, cambiaría
    // solo al guardar sin que nadie lo pida.
    statusOptions() {
      return this.instructional_statuses
        .filter(
          (x) => x.is_active || x.id == this.evaluation.instructional_status
        )
        .map((status) => ({ id: status.id, label: status.name }));
    },
    matterStudyUnits() {
      if (!this.matter_id) return [];
      return this.study_units.filter((x) => x.matter == this.matter_id);
    },
    // Los RA de la evaluación se derivan de sus IL: no son un campo (E12).
    selectedStudyUnits() {
      const selected = this.evaluation.evaluation_criterias_micro || [];
      const by_id = new Map(
        this.evaluationCriteriasMicros.map((x) => [x.id, x])
      );
      const study_unit_ids = new Set();
      selected.forEach((id) => {
        const indicator = by_id.get(id);
        if (indicator) study_unit_ids.add(indicator.study_unit);
      });
      return [...study_unit_ids];
    },
    contentOptions() {
      if (this.selectedStudyUnits.length === 0) return [];
      const study_unit_by_id = new Map(
        this.matterStudyUnits.map((study_unit) => [study_unit.id, study_unit])
      );
      return this.study_unit_contents
        .filter(
          (x) =>
            this.selectedStudyUnits.includes(x.study_unit) &&
            study_unit_by_id.has(x.study_unit)
        )
        .map((content) => {
          const study_unit = study_unit_by_id.get(content.study_unit);
          const prefix = `${this.getCompetenceUnitOrder(
            study_unit.temp_competence_unit
          )}${study_unit.order}`;
          return {
            id: content.id,
            study_unit_order: study_unit.order,
            label: `${this.$t(
              "reusable.common.numberAbbreviation"
            )}${prefix} ${this.$htmlToPlainText(content.content)}`,
          };
        })
        .sort(
          (a, b) =>
            a.study_unit_order - b.study_unit_order ||
            a.label.localeCompare(b.label)
        );
    },
    selectedStrategies() {
      const selected = this.evaluation.methodological_strategies || [];
      return this.methodologicalStrategies
        .filter((x) => selected.includes(x.id))
        .sort((a, b) => a.name.localeCompare(b.name));
    },
    // Las que la Asignatura declaró como mínimas: el selector las destaca.
    matterStrategyIds() {
      const matter = this.matters.find((x) => x.id == this.matter_id);
      return matter ? matter.methodological_strategies || [] : [];
    },
    procedureEvidenceOptions() {
      return [...this.procedureEvidences]
        .map((evidence) => ({
          id: evidence.id,
          name: evidence.name,
          tooltip: this.evidenceTooltip(evidence),
        }))
        .sort((a, b) => a.name.localeCompare(b.name));
    },
    // Crear una estrategia toca el catálogo de la institución, no esta evaluación: se pide el
    // permiso del catálogo.
    canCreateStrategies() {
      return this.$hasPermissions("mesh.add_methodologicalstrategy");
    },
    strategiesModalId() {
      return `evaluation-instructional-strategies-modal-${this.unique_id}`;
    },
    professorModalId() {
      return `evaluation-instructional-professor-modal-${this.unique_id}`;
    },
  },
  watch: {
    // Al cambiar los IL cambian los RA, y con ellos los contenidos válidos. Se descartan los que
    // ya no correspondan; los aún no cargados se conservan, para no perder la selección guardada
    // mientras llega el fetch.
    selectedStudyUnits(study_unit_ids) {
      if (!this.evaluation.contents) return;
      this.evaluation.contents = this.evaluation.contents.filter((id) => {
        const content = this.study_unit_contents.find((x) => x.id == id);
        return !content || study_unit_ids.includes(content.study_unit);
      });
    },
  },
  created() {
    this.fetchCatalogs();
  },
  methods: {
    // El componente se monta en DOS vistas: el Diseño Instruccional, que ya trae todo esto, y el
    // Programa de Asignatura, que no sabe nada del DI. Se pide lo que falte para que funcione en
    // las dos sin que el formulario del Programa tenga que enterarse.
    //
    // Se usa FETCH y no RESOLVE para el diseño: `resolve` lo CREA si no existe, y abrir una
    // evaluación en el Programa no es motivo para crearle un diseño a la asignatura.
    fetchCatalogs() {
      const level = this.egress_profile_matter_id
        ? { egress_profile_matter_id: this.egress_profile_matter_id }
        : { matter_id: this.matter_id };
      this.$store.dispatch(names.FETCH_INSTRUCTIONAL_DESIGNS, level);
      // `force`: mismo motivo que en Instructional.vue — la caché de la acción no sabe
      // de instituciones y el getter sí, así que un store poblado dejaba el selector vacío.
      this.$store.dispatch(names.FETCH_INSTRUCTIONAL_METHODS, true);
      this.$store.dispatch(names.FETCH_INSTRUCTIONAL_STATUSES);
      this.$store.dispatch(names.FETCH_METHODOLOGICAL_STRATEGIES);
      this.$store.dispatch(names.FETCH_PROCEDURE_EVIDENCES);
      if (!this.matter_id) return;
      this.$store.dispatch(names.FETCH_EVALUATION_CRITERIAS, {
        matter_id: this.matter_id,
      });
      // Los contenidos sólo se piden por Unidad de Estudio: se traen los de todas para poder
      // ofrecerlos según los IL que la evaluación tenga elegidos.
      this.$store
        .dispatch(names.FETCH_STUDY_UNITS, this.matter_id)
        .then((study_units) => {
          study_units.forEach((study_unit) => {
            this.$store.dispatch(names.FETCH_CONTENTS, study_unit.id);
          });
        });
    },
    onStrategiesSaved(strategy_ids) {
      this.evaluation.methodological_strategies = strategy_ids;
      this.$bvModal.hide(this.strategiesModalId);
    },
    onProfessorInstructionsSaved(content) {
      this.evaluation.professor_instructions = content;
      this.$bvModal.hide(this.professorModalId);
    },
    // La descripción de una evidencia puede ser larga: al tooltip va recortada, o tapa la
    // pantalla y no se puede cerrar.
    evidenceTooltip(evidence) {
      const plain = this.$htmlToPlainText(evidence.description || "").trim();
      if (!plain) return evidence.name;
      if (plain.length <= 180) return plain;
      return `${plain.slice(0, 180).trim()}…`;
    },
    getCompetenceUnitOrder(temp_competence_unit_id) {
      const competence_unit = this.competence_units.find(
        (x) => x.id == temp_competence_unit_id
      );
      return competence_unit ? competence_unit.order + "." : "";
    },
  },
};
</script>

<style scoped>
.form-description {
  border: 1px solid var(--kl-table-border, #d9e0e0);
  border-radius: var(--kl-radius-md, 6px);
  padding: 0.35rem 0.55rem;
  min-height: 2.1rem;
  cursor: pointer;
  font-size: 0.85rem;
}
.form-description:hover {
  background-color: #f2f8f6;
}
/* Resumen de lo elegido + el botón que abre el selector, en una sola línea. */
.kl-strategy-box {
  display: flex;
  align-items: center;
  gap: 0.4rem;
  border: 1px solid var(--kl-table-border, #d9e0e0);
  border-radius: var(--kl-radius-md, 6px);
  padding: 0.3rem 0.5rem;
  min-height: 2.1rem;
}
.kl-strategy-chips {
  display: flex;
  flex-wrap: wrap;
  gap: 0.25rem;
}
.kl-chip {
  display: inline-block;
  background-color: var(--kl-chip-bg, #e0f1ff);
  color: var(--kl-chip-text, #006ec8);
  border: 1px solid var(--kl-chip-border, #b6d9f5);
  border-radius: var(--kl-radius-pill, 999px);
  padding: var(--kl-chip-padding, 2px 8px);
  font-size: var(--kl-chip-fs, 0.72rem);
  font-weight: var(--kl-chip-fw, 600);
}
/* Las etiquetas seleccionadas del v-select toman el mismo chip, en vez del gris de vue-select. */
.kl-chip-select >>> .vs__selected {
  background-color: var(--kl-chip-bg, #e0f1ff);
  color: var(--kl-chip-text, #006ec8);
  border: 1px solid var(--kl-chip-border, #b6d9f5);
  border-radius: var(--kl-radius-pill, 999px);
  padding: var(--kl-chip-padding, 2px 8px);
  font-size: var(--kl-chip-fs, 0.72rem);
  font-weight: var(--kl-chip-fw, 600);
}
.kl-chip-select >>> .vs__deselect {
  fill: var(--kl-chip-text, #006ec8);
}
</style>
